/**
 * Review detail — ratings, qualitative feedback, goals, 360° feedback list,
 * and a form to add feedback. Share/Unshare + Edit for Admin/Manager.
 *
 * Visibility is enforced server-side: employees only reach here for Shared
 * reviews about themselves. The add-feedback form is available to everyone
 * who can see the review (peers, manager, self-review by relationship).
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, Eye, EyeOff, Star, MessageSquarePlus, Pencil, Save, X, Trash2, FileDown } from 'lucide-react';
import {
  useReview,
  useShareReview,
  useUnshareReview,
  useUpdateReview,
  useReviewFeedback,
  useCreateFeedback,
  useDeleteReview,
} from '../api.js';
import { useAuthStore } from '../../../store/auth.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Select, Textarea, Field } from '../../../shell/Field.js';
import { formatDate, formatDateTime } from '../../../lib/format.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { FEEDBACK_RELATIONSHIP, type FeedbackRelationship } from '@shared/enums/index.js';
import type { ReviewRatings, WeeklyScorecard } from '@shared/types';
import { isWeeklyScorecard } from '@shared/util/weekly-scorecard.js';
import {
  WeeklyScorecardFields,
  WeeklyScorecardReadonly,
  emptyWeeklyDraft,
  weeklyDraftFromScorecard,
  draftToWeeklyScorecard,
  type WeeklyScorecardDraft,
} from '../components/WeeklyScorecardFields.js';

function StarRow({ value }: { value: number }) {
  return (
    <span className="inline-flex" aria-label={`${value} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={
            i <= value
              ? 'h-4 w-4 fill-amber-400 text-amber-400'
              : 'h-4 w-4 text-slate-300 dark:text-slate-600'
          }
        />
      ))}
    </span>
  );
}

export function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: review, isLoading } = useReview(id);
  const { data: feedbackData } = useReviewFeedback(id);
  const shareMutation = useShareReview();
  const unshareMutation = useUnshareReview();
  const updateMutation = useUpdateReview();
  const deleteMutation = useDeleteReview();
  const handleDelete = async () => {
    if (!review) return;
    if (!window.confirm('Delete this review? This cannot be undone.')) return;
    try {
      await deleteMutation.mutateAsync(review.id);
      toast('success', 'Review deleted.');
      navigate('/reviews');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to delete review.';
      toast('error', msg);
    }
  };

  const createFeedback = useCreateFeedback(id ?? '');

  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [performance, setPerformance] = useState(3);
  const [culture, setCulture] = useState(3);
  const [communication, setCommunication] = useState(3);
  const [dependability, setDependability] = useState(3);
  const [feedback, setFeedback] = useState('');
  const [goals, setGoals] = useState('');
  const [weekly, setWeekly] = useState<WeeklyScorecardDraft>(emptyWeeklyDraft());

  useEffect(() => {
    if (!review) return;
    if (isWeeklyScorecard(review.ratings)) {
      setWeekly(weeklyDraftFromScorecard(review.ratings));
      setFeedback(review.ratings.wins);
      setGoals(review.ratings.actions);
      return;
    }
    setPerformance(review.ratings.performance);
    setCulture(review.ratings.culture);
    setCommunication(review.ratings.communication);
    setDependability(review.ratings.dependability);
    setFeedback(review.qualitativeFeedback ?? '');
    setGoals(review.goalsForNextPeriod ?? '');
  }, [review]);

  if (isLoading || !review) {
    return <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>;
  }

  const canManage = user?.role === 'Admin' || user?.role === 'Manager';
  const weeklyReview = isWeeklyScorecard(review.ratings);
  const ratingEntries = weeklyReview
    ? []
    : (Object.entries(review.ratings) as [keyof ReviewRatings, number][]);

  const handleShare = async () => {
    try {
      await shareMutation.mutateAsync(review.id);
      toast('success', 'Review shared with employee.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to share review.';
      toast('error', msg);
    }
  };

  const handleUnshare = async () => {
    try {
      await unshareMutation.mutateAsync(review.id);
      toast('success', 'Review unshared.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to unshare review.';
      toast('error', msg);
    }
  };

  const handleSave = async () => {
    try {
      const ratings = weeklyReview
        ? draftToWeeklyScorecard(weekly)
        : { performance, culture, communication, dependability };
      await updateMutation.mutateAsync({
        id: review.id,
        body: {
          ratings,
          qualitativeFeedback: weeklyReview ? weekly.wins : feedback,
          goalsForNextPeriod: weeklyReview ? weekly.actions : goals,
        },
      });
      toast('success', 'Review updated.');
      setEditing(false);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to update review.';
      toast('error', msg);
    }
  };

  const cancelEdit = () => {
    if (isWeeklyScorecard(review.ratings)) {
      setWeekly(weeklyDraftFromScorecard(review.ratings));
      setFeedback(review.ratings.wins);
      setGoals(review.ratings.actions);
    } else {
      setPerformance(review.ratings.performance);
      setCulture(review.ratings.culture);
      setCommunication(review.ratings.communication);
      setDependability(review.ratings.dependability);
      setFeedback(review.qualitativeFeedback ?? '');
      setGoals(review.goalsForNextPeriod ?? '');
    }
    setEditing(false);
  };

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/reviews')}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Reviews
      </button>

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">{weeklyReview ? 'Weekly 1:1' : 'Review Details'}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge tone="blue">{review.type}</Badge>
            <Badge tone={review.status === 'Shared' ? 'green' : 'yellow'}>{review.status}</Badge>
            {review.visibleToEmployeeAt && (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                Shared {formatDate(review.visibleToEmployeeAt)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {review.status === 'Shared' && (
            <a
              href={`/api/action-pdfs/review/${review.id}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="secondary">
                <FileDown className="h-4 w-4" /> Download PDF
              </Button>
            </a>
          )}
          {canManage && (
            <>
              {!editing ? (
                <>
                  <Button variant="secondary" onClick={() => setEditing(true)}>
                    <Pencil className="h-4 w-4" /> Edit
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDelete}
                    loading={deleteMutation.isPending}
                    disabled={editing}
                  >
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={cancelEdit}>
                    <X className="h-4 w-4" /> Cancel
                  </Button>
                  <Button onClick={handleSave} loading={updateMutation.isPending}>
                    <Save className="h-4 w-4" /> Save
                  </Button>
                </>
              )}
              {review.visibleToEmployeeAt ? (
                <Button
                  variant="secondary"
                  onClick={handleUnshare}
                  loading={unshareMutation.isPending}
                  disabled={editing}
                >
                  <EyeOff className="h-4 w-4" /> Unshare
                </Button>
              ) : (
                <Button onClick={handleShare} loading={shareMutation.isPending} disabled={editing}>
                  <Eye className="h-4 w-4" /> Share with Employee
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Ratings / Weekly scorecard */}
      {weeklyReview ? (
        editing ? (
          <WeeklyScorecardFields value={weekly} onChange={setWeekly} />
        ) : (
          <WeeklyScorecardReadonly scorecard={review.ratings as WeeklyScorecard} />
        )
      ) : editing ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <RatingSelect label="Performance" value={performance} onChange={setPerformance} />
          <RatingSelect label="Culture" value={culture} onChange={setCulture} />
          <RatingSelect label="Communication" value={communication} onChange={setCommunication} />
          <RatingSelect label="Dependability" value={dependability} onChange={setDependability} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {ratingEntries.map(([key, value]) => (
            <div key={key} className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{key}</p>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{value}/5</span>
              </div>
              <div className="mt-2">
                <StarRow value={value} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!weeklyReview && (
        <>
          <div>
            <h2 className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">Qualitative Feedback</h2>
            {editing ? (
              <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={4} />
            ) : review.qualitativeFeedback ? (
              <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                {review.qualitativeFeedback}
              </p>
            ) : (
              <p className="text-sm text-slate-400 dark:text-slate-500">No qualitative feedback.</p>
            )}
          </div>

          <div>
            <h2 className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">Goals for Next Period</h2>
            {editing ? (
              <Textarea value={goals} onChange={(e) => setGoals(e.target.value)} rows={3} />
            ) : review.goalsForNextPeriod ? (
              <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                {review.goalsForNextPeriod}
              </p>
            ) : (
              <p className="text-sm text-slate-400 dark:text-slate-500">No goals set.</p>
            )}
          </div>
        </>
      )}

      {/* 360 Feedback */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            360° Feedback {feedbackData ? `(${feedbackData.items.length})` : ''}
          </h2>
          <Button size="sm" variant="secondary" onClick={() => setShowFeedbackForm((v) => !v)} disabled={editing}>
            <MessageSquarePlus className="h-4 w-4" /> Add Feedback
          </Button>
        </div>

        {showFeedbackForm && (
          <FeedbackForm
            create={createFeedback}
            onDone={() => setShowFeedbackForm(false)}
          />
        )}

        {feedbackData && feedbackData.items.length > 0 ? (
          <div className="space-y-2">
            {feedbackData.items.map((f) => (
              <div key={f.id} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center justify-between">
                  <Badge tone="slate">{f.relationship}</Badge>
                  <span className="text-xs text-slate-400 dark:text-slate-500">{formatDateTime(f.submittedAt)}</span>
                </div>
                {f.ratings && (
                  <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-slate-500 dark:text-slate-400 sm:grid-cols-4">
                    {(Object.entries(f.ratings) as [keyof ReviewRatings, number][]).map(([k, v]) => (
                      <span key={k}>
                        {k}: <span className="font-medium text-slate-700 dark:text-slate-300">{v}/5</span>
                      </span>
                    ))}
                  </div>
                )}
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{f.comments}</p>
              </div>
            ))}
          </div>
        ) : (
          !showFeedbackForm && (
            <p className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400 dark:border-slate-700 dark:text-slate-500">
              No feedback yet.
            </p>
          )
        )}
      </div>
    </div>
  );
}

function RatingSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <Select label={label} value={String(value)} onChange={(e) => onChange(Number(e.target.value))}>
      {[1, 2, 3, 4, 5].map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </Select>
  );
}

function FeedbackForm({
  create,
  onDone,
}: {
  create: ReturnType<typeof useCreateFeedback>;
  onDone: () => void;
}) {
  const [relationship, setRelationship] = useState<FeedbackRelationship>('Peer');
  const [comments, setComments] = useState('');

  const handleSubmit = async () => {
    if (!comments.trim()) return;
    try {
      await create.mutateAsync({ relationship, ratings: null, comments: comments.trim() });
      toast('success', 'Feedback added.');
      setComments('');
      onDone();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to add feedback.';
      toast('error', msg);
    }
  };

  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="space-y-3">
        <Select
          label="Your relationship"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value as FeedbackRelationship)}
        >
          {FEEDBACK_RELATIONSHIP.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </Select>
        <Field label="Comments">
          <Textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Share your observations…"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" type="button" onClick={onDone}>Cancel</Button>
          <Button size="sm" type="button" onClick={handleSubmit} loading={create.isPending} disabled={!comments.trim()}>
            Submit Feedback
          </Button>
        </div>
      </div>
    </div>
  );
}
